import styled from '@emotion/styled';
import {
  palette,
  status,
  spacing,
  radius,
  shadow,
  typography,
  layout,
  transition,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';

/**
 * Styled layer for the SHIFT HANDOVER record.
 *
 * ═════════════ WHERE THE AURORA IS, AND WHERE IT DELIBERATELY IS NOT ═════════
 * design_system.md §10 reserves `gradient.aurora` exclusively for Lobbero IQ, so
 * the split in this feature runs along one line: DID A MODEL WRITE THIS?
 *
 *   AURORA  — the summary card's header. It is reused from iq-shared.styled
 *             (AuroraHeader / IqCard) rather than redrawn here, so the handover
 *             is visibly the same kind of object as the on-demand summary.
 *   NO AURORA — the chain-of-custody strip (who → who, when, which window), the
 *             clamp warning, the read receipt, and the recipient picker. Those
 *             are facts out of `shift_handoffs` and `users`; no model produced a
 *             character of them, and painting them with the AI mark would say a
 *             model decided who handed over to whom.
 *
 * That is the same rule shifts.styled.tsx states for the clock, applied to the
 * one surface where AI output and hard record sit inside a single card.
 */

/* ───────────────────────── CHAIN OF CUSTODY ───────────────────────── */

/* Sits UNDER the aurora header and ABOVE the summary, on the muted inset
 * surface — the reader learns who and when before they read a word of prose. */
export const CustodyBar = styled.div`
  background: ${palette.surfaceMuted};
  border-bottom: 1px solid ${palette.line};
  padding: ${spacing.xs} ${spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const CustodyLine = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  margin: 0;
`;

export const Person = styled.strong`
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

/* A name the product could not resolve. Rendered in the muted voice so it reads
 * as "we do not know", never as somebody actually called "Unknown". */
export const UnknownPerson = styled.em`
  font-style: normal;
  font-weight: ${typography.weight.regular};
  color: ${palette.slateLight};
`;

export const CustodyMeta = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.half} ${spacing.xs};
`;

export const MetaMono = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

/* ───────────────────────────── BANDS ───────────────────────────── */

/* A statement the reader must meet BEFORE the summary, not after: the previous
 * shift was never clocked out, or the summary is a floor and here is why.
 *
 * Warning, not alert. A forgotten clock-out is an ordinary human oversight and
 * painting it red every morning teaches the desk to ignore red — the same
 * reasoning shifts.styled.tsx gives for ResolvePanel. */
export const NoticeBand = styled.div<{ $tone: keyof typeof status }>`
  background: ${p => status[p.$tone].bg};
  color: ${p => status[p.$tone].fg};
  border-left: 3px solid ${p => status[p.$tone].dot};
  border-radius: ${radius.xs};
  padding: ${spacing.xs} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const BandTitle = styled.span`
  font-weight: ${typography.weight.semibold};
`;

/* ─────────────────────────── THE INBOX ─────────────────────────── */

export const InboxWrap = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

export const InboxHead = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.xs};
`;

export const InboxTitle = styled.h3`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  margin: 0;
  flex: 1;
  min-width: 0;
`;

export const TabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.half};
`;

/* Inbox / outbox. Active state is a tint + border, NEVER a weight change —
 * design_system §9 Navigation: switching weight reflows the label and shifts
 * everything beside it on every click. */
export const Tab = styled.button<{ $active?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  min-height: ${layout.compactControlHeight};
  /* Coarse pointer: keep the compact look, meet the 44px floor. A desk tablet
     is this product's primary device, so a 32px control here is the common
     case and not the edge one. NO BACKTICKS IN THIS COMMENT - it lives inside a
     styled template literal. */
  ${touch} {
    height: auto;
    min-height: ${layout.touchTarget};
  }

  padding: 0 ${spacing.sm};
  border-radius: ${radius.pill};
  border: 1px solid ${p => (p.$active ? palette.iris : palette.line)};
  background: ${p => (p.$active ? palette.irisTint : palette.surface)};
  color: ${p => (p.$active ? status.brand.fg : palette.slate)};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/* A row in the "past handovers" list. Not a table: below md a table of four
 * columns reflows to nonsense, and this is already one line per record. */
export const HistoryRow = styled.button<{ $active?: boolean; $unread?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  text-align: left;
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing.half} ${spacing.xs};
  min-height: ${layout.controlHeight};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  border: 1px solid
    ${p => (p.$active ? palette.iris : palette.line)};
  background: ${p =>
    p.$active ? palette.irisTintSoft : palette.surface};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.inkSoft};
  transition: ${transition.colors};

  /* Unread is carried by a WORD as well as this rule (design_system §7
   * accessibility: never rely on colour or motion alone to convey state). */
  ${p => (p.$unread ? `border-left: 3px solid ${palette.iris};` : '')}

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
  }
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const RowMain = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${palette.ink};
  font-weight: ${typography.weight.medium};
`;

export const RowWhen = styled.span`
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slate};
`;

export const HistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const InboxNote = styled.p`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
  line-height: ${typography.leading.normal};
  margin: 0;
`;

/* ─────────────────────── SOURCE REFERENCES ─────────────────────── */

/* NOT a button, unlike iq-shared's SourceChip.
 *
 * The chip in the on-demand summary scrolls to the log entry it cites, because
 * that response ships the entries alongside it. A handover ships the SNAPSHOT
 * ONLY — the server deliberately does not re-fetch or re-resolve the window on
 * read, so there is no corpus on this screen to scroll to. Rendering a chip that
 * looks clickable and does nothing would be worse than rendering a reference. */
export const SourceRef = styled.span`
  border: 1px solid ${palette.line};
  background: ${status.neutral.bg};
  color: ${status.neutral.fg};
  font-family: ${typography.mono};
  font-size: ${typography.size.xxs}px;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
`;

/* ───────────────────────── THE FAB BADGE ───────────────────────── */

/* The arrival cue that does NOT hijack the screen. D-25 wants the recipient to
 * have the handover without asking; it does not want a modal thrown in the face
 * of somebody who opened the app to check a delivery. So the count rides the
 * existing IQ entry point, and opening it is still the reader's decision. */
export const FabBadge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 22px;
  height: 22px;
  padding: 0 ${spacing.half};
  border-radius: ${radius.pill};
  background: ${palette.alertStrong};
  color: ${palette.white};
  border: 2px solid ${palette.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  line-height: 1;
  box-shadow: ${shadow.sm};
`;

/* ────────────────────── THE OUTGOING RECEIPT ───────────────────── */

export const ReceiptWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  ${down('sm')} {
    gap: ${spacing.half};
  }
`;

/* A heading INSIDE the handover card — "Needs attention" over a floor
   record's flags. The caps treatment every small section label on this card
   uses, so it reads as a label and not as a second title. */
export const SubHead = styled.h4`
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;
